import React, { useState, useEffect, useCallback } from 'react';
import { safeTrim } from '../utils/validation';
import { useApp } from '../contexts/AppContext';
import { useAuth } from '../contexts/AuthContext';
import MiniOdpMap from '../components/MiniOdpMap';
import {
  Plus, Trash2, Edit2, Search, MapPin, Network, Users, Filter,
  ChevronLeft, ChevronRight, Eye, Link2, Unlink, FileText, FileSpreadsheet, QrCode, Download, Copy
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const statusConfig = {
  active: { label: 'Aktif', color: 'bg-emerald-500/10 text-emerald-600 border-emerald-200' },
  full: { label: 'Penuh', color: 'bg-red-500/10 text-red-600 border-red-200' },
  maintenance: { label: 'Maintenance', color: 'bg-amber-500/10 text-amber-600 border-amber-200' },
  inactive: { label: 'Nonaktif', color: 'bg-slate-500/10 text-slate-500 border-slate-200' },
};

const ODPManagement = () => {
  const { selectedBranch } = useApp();
  const { user } = useAuth();
  const userBranchId = user?.branch_id ? String(user.branch_id) : null;
  const isBranchScoped = ['kepala_cabang', 'teknisi', 'customer service'].includes(String(user?.role || '').toLowerCase()) && userBranchId;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [filters, setFilters] = useState({ branch_id: isBranchScoped ? userBranchId : '', owner_id: '', status: '', search: '' });
  const [filterOptions, setFilterOptions] = useState({ branches: [], owners: [] });
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [showForm, setShowForm] = useState(false);
  const [showDetail, setShowDetail] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [detailData, setDetailData] = useState(null);
  const [showLinkDialog, setShowLinkDialog] = useState(false);
  const [showQrDialog, setShowQrDialog] = useState(false);
  const [qrState, setQrState] = useState({ loading: false, url: null, objectUrl: null });
  const [customerSearch, setCustomerSearch] = useState('');
  const [customerResults, setCustomerResults] = useState([]);
  const [linkPort, setLinkPort] = useState('');
  const [formData, setFormData] = useState({
    name: '', address: '', latitude: '', longitude: '',
    capacity: '', branch_id: isBranchScoped ? userBranchId : '', branch_name: '', owner_id: '',
    owner_name: '', status: 'active', description: ''
  });

  const token = localStorage.getItem('token');
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, per_page: 20 });
      // Global branch filter from navbar dropdown (overrides local if set)
      const effectiveBranch = (selectedBranch && selectedBranch !== 'all') ? selectedBranch : filters.branch_id;
      if (effectiveBranch) params.append('branch_id', effectiveBranch);
      if (filters.owner_id) params.append('owner_id', filters.owner_id);
      if (filters.status) params.append('status', filters.status);
      if (filters.search) params.append('search', filters.search);

      const res = await fetch(`${API_URL}/api/odp-pop?${params}`, { headers });
      const data = await res.json();
      if (data.success) {
        setItems(data.data || []);
        setTotal(data.pagination?.total || 0);
        setTotalPages(data.pagination?.total_pages || 1);
      }
    } catch (e) {
      toast.error('Gagal memuat data ODP/POP');
    }
    setLoading(false);
  }, [page, filters, selectedBranch]);

  const fetchStats = useCallback(async () => {
    try {
      const qs = (selectedBranch && selectedBranch !== 'all') ? `?branch_id=${selectedBranch}` : '';
      const res = await fetch(`${API_URL}/api/odp-pop/statistics${qs}`, { headers });
      const data = await res.json();
      if (data.success) setStats(data.data);
    } catch (e) { /* silent */ }
  }, [selectedBranch]);

  const fetchFilterOptions = async () => {
    try {
      const res = await fetch(`${API_URL}/api/odp-pop/filters/options`, { headers });
      const data = await res.json();
      if (data.success) setFilterOptions({ branches: data.branches, owners: data.owners });
    } catch (e) { /* silent */ }
  };

  useEffect(() => { fetchItems(); }, [fetchItems]);
  useEffect(() => { fetchStats(); }, [fetchStats]);
  useEffect(() => { fetchFilterOptions(); }, []);

  // Auto-open ODP detail from query param (e.g. QR scan: /app/odp-management?odp=7)
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const odpId = params.get('odp');
      if (odpId && /^\d+$/.test(odpId)) {
        openDetail(parseInt(odpId, 10));
      }
    } catch (_) { /* ignore */ }
  }, []);

  const handleSave = async () => {
    // Frontend validation
    if (!formData.name || !safeTrim(formData.name)) {
      toast.error('Nama ODP wajib diisi');
      return;
    }
    if (formData.latitude && isNaN(parseFloat(formData.latitude))) {
      toast.error('Latitude tidak valid');
      return;
    }
    if (formData.longitude && isNaN(parseFloat(formData.longitude))) {
      toast.error('Longitude tidak valid');
      return;
    }
    if (formData.capacity && isNaN(parseInt(formData.capacity))) {
      toast.error('Kapasitas Port harus berupa angka');
      return;
    }

    try {
      // Build payload - only include filled fields to avoid backend type errors
      const payload = { name: safeTrim(formData.name), status: formData.status || 'active' };
      if (safeTrim(formData.address)) payload.address = safeTrim(formData.address);
      if (formData.latitude) payload.latitude = parseFloat(formData.latitude);
      if (formData.longitude) payload.longitude = parseFloat(formData.longitude);
      if (formData.capacity) payload.capacity = parseInt(formData.capacity);
      if (formData.branch_id) payload.branch_id = parseInt(formData.branch_id);
      // Auto-fill branch_name for branch-scoped users (dropdown disabled)
      let effectiveBranchName = safeTrim(formData.branch_name);
      if (isBranchScoped && formData.branch_id && !effectiveBranchName) {
        const b = filterOptions.branches.find(x => String(x.branch_id) === String(formData.branch_id));
        if (b) effectiveBranchName = b.branch_name;
      }
      if (effectiveBranchName) payload.branch_name = effectiveBranchName;
      if (formData.owner_id) payload.owner_id = formData.owner_id;
      if (safeTrim(formData.owner_name)) payload.owner_name = safeTrim(formData.owner_name);
      if (safeTrim(formData.description)) payload.description = safeTrim(formData.description);

      const url = editItem ? `${API_URL}/api/odp-pop/${editItem.id}` : `${API_URL}/api/odp-pop`;
      const method = editItem ? 'PUT' : 'POST';
      const res = await fetch(url, { method, headers, body: JSON.stringify(payload) });
      const data = await res.json();
      if (res.ok && data.success) {
        toast.success(data.message || (editItem ? 'ODP berhasil diperbarui' : 'ODP berhasil ditambahkan'));
        setShowForm(false);
        setEditItem(null);
        fetchItems();
        fetchStats();
        fetchFilterOptions();
      } else {
        // Friendly error parsing
        let msg = 'Gagal menyimpan data';
        if (typeof data.detail === 'string') {
          msg = data.detail;
        } else if (Array.isArray(data.detail) && data.detail.length > 0) {
          const first = data.detail[0];
          const field = (first.loc || []).slice(-1)[0] || 'field';
          const fieldMap = { name: 'Nama ODP', address: 'Alamat', latitude: 'Latitude', longitude: 'Longitude', capacity: 'Kapasitas Port', branch_name: 'Cabang', owner_name: 'Owner', status: 'Status', description: 'Deskripsi' };
          msg = `${fieldMap[field] || field} tidak valid: ${first.msg || 'harap periksa kembali'}`;
        }
        toast.error(msg);
      }
    } catch (e) {
      toast.error('Terjadi kesalahan saat menyimpan');
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Hapus ODP/POP ini?')) return;
    try {
      const res = await fetch(`${API_URL}/api/odp-pop/${id}`, { method: 'DELETE', headers });
      const data = await res.json();
      if (data.success) { toast.success(data.message); fetchItems(); fetchStats(); }
    } catch (e) { toast.error('Gagal menghapus'); }
  };

  const openDetail = async (id) => {
    try {
      const res = await fetch(`${API_URL}/api/odp-pop/${id}`, { headers });
      const data = await res.json();
      if (data.success) { setDetailData(data.data); setShowDetail(true); }
    } catch (e) { toast.error('Gagal memuat detail'); }
  };

  const openEdit = (item) => {
    setEditItem(item);
    setFormData({
      name: item.name || '', address: item.address || '',
      latitude: item.latitude || '', longitude: item.longitude || '',
      capacity: item.capacity || '', branch_id: item.branch_id || '',
      branch_name: item.branch_name || '', owner_id: item.owner_id || '',
      owner_name: item.owner_name || '', status: item.status || 'active',
      description: item.description || ''
    });
    setShowForm(true);
  };

  const openAdd = () => {
    setEditItem(null);
    setFormData({ name: '', address: '', latitude: '', longitude: '', capacity: '',
      branch_id: isBranchScoped ? userBranchId : '', branch_name: '', owner_id: '', owner_name: '', status: 'active', description: '' });
    setShowForm(true);
  };

  const searchCustomers = async (q) => {
    setCustomerSearch(q);
    if (q.length < 2) { setCustomerResults([]); return; }
    try {
      const res = await fetch(`${API_URL}/api/v2/customers?search=${q}&limit=10`, { headers });
      const data = await res.json();
      setCustomerResults(data.data?.customers || data.data || []);
    } catch (e) { /* silent */ }
  };

  const linkCustomer = async (customerId) => {
    if (!detailData) return;
    try {
      const res = await fetch(`${API_URL}/api/odp-pop/${detailData.id}/link-customer`, {
        method: 'POST', headers,
        body: JSON.stringify({ customer_id: customerId, port_number: linkPort ? parseInt(linkPort) : null })
      });
      const data = await res.json();
      if (data.success) {
        toast.success(data.message);
        setShowLinkDialog(false);
        setCustomerSearch('');
        setLinkPort('');
        openDetail(detailData.id);
        fetchItems();
        fetchStats();
      } else {
        toast.error(data.detail || 'Gagal menghubungkan');
      }
    } catch (e) { toast.error('Error'); }
  };

  const unlinkCustomer = async (customerId) => {
    if (!detailData || !confirm('Lepas customer dari ODP ini?')) return;
    try {
      const res = await fetch(`${API_URL}/api/odp-pop/${detailData.id}/unlink-customer/${customerId}`, {
        method: 'DELETE', headers
      });
      const data = await res.json();
      if (data.success) { toast.success(data.message); openDetail(detailData.id); fetchItems(); fetchStats(); }
    } catch (e) { toast.error('Error'); }
  };

  const exportOdp = async (fmt) => {
    if (!detailData) return;
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/odp-pop/${detailData.id}/export?format=${fmt}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) {
        const errJson = await res.json().catch(() => ({}));
        throw new Error(errJson.detail || 'Export gagal');
      }
      const blob = await res.blob();
      const dispo = res.headers.get('Content-Disposition') || '';
      const match = dispo.match(/filename="?([^";]+)"?/i);
      const filename = match ? match[1] : `ODP_${detailData.name}_${Date.now()}.${fmt === 'pdf' ? 'pdf' : 'xlsx'}`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success(`Export ${fmt.toUpperCase()} berhasil`);
    } catch (e) {
      toast.error(e.message || 'Export gagal');
    }
  };

  const openQr = async () => {
    if (!detailData) return;
    setShowQrDialog(true);
    setQrState({ loading: true, url: null, objectUrl: null });
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/odp-pop/${detailData.id}/qr?size=12`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Gagal memuat QR');
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      const url = res.headers.get('X-ODP-URL') || '';
      setQrState({ loading: false, url, objectUrl });
    } catch (e) {
      toast.error(e.message || 'Gagal memuat QR');
      setShowQrDialog(false);
      setQrState({ loading: false, url: null, objectUrl: null });
    }
  };

  const downloadQr = () => {
    if (!qrState.objectUrl || !detailData) return;
    const a = document.createElement('a');
    a.href = qrState.objectUrl;
    a.download = `QR_${detailData.name}_${detailData.id}.png`;
    document.body.appendChild(a); a.click(); a.remove();
    toast.success('QR berhasil diunduh');
  };

  const copyQrUrl = async () => {
    if (!qrState.url) return;
    try {
      await navigator.clipboard.writeText(qrState.url);
      toast.success('URL disalin ke clipboard');
    } catch {
      toast.error('Gagal menyalin URL');
    }
  };

  const s = stats?.summary || {};

  return (
    <div data-testid="odp-management" className="space-y-6 p-3 sm:p-5 lg:p-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[
          { label: 'Total ODP', value: s.total || 0, icon: Network, cls: 'text-blue-600 bg-blue-50' },
          { label: 'Aktif', value: s.active || 0, icon: MapPin, cls: 'text-emerald-600 bg-emerald-50' },
          { label: 'Penuh', value: s.full_count || 0, icon: MapPin, cls: 'text-red-600 bg-red-50' },
          { label: 'Maintenance', value: s.maintenance || 0, icon: MapPin, cls: 'text-amber-600 bg-amber-50' },
          { label: 'Port Terpakai', value: `${s.total_used_ports || 0}/${s.total_capacity || 0}`, icon: Users, cls: 'text-purple-600 bg-purple-50' },
        ].map((c, i) => (
          <div key={i} className="bg-white rounded-lg border p-4 flex items-center gap-3 dark:bg-slate-800">
            <div className={`p-2 rounded-lg ${c.cls}`}><c.icon className="w-5 h-5" /></div>
            <div>
              <p className="text-xs text-slate-500 dark:text-slate-400">{c.label}</p>
              <p className="text-lg font-bold text-slate-800 dark:text-slate-200">{c.value}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Filters & Actions */}
      <div className="bg-white rounded-lg border p-4 dark:bg-slate-800">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2 flex-1 min-w-[200px]">
            <Search className="w-4 h-4 text-slate-400 dark:text-slate-500" />
            <Input data-testid="odp-search" placeholder="Cari nama/area..."
              value={filters.search} onChange={e => { setFilters(f => ({ ...f, search: e.target.value })); setPage(1); }}
              className="h-9" />
          </div>
          <select data-testid="odp-filter-status" className="h-9 border rounded-md px-3 text-sm"
            value={filters.status} onChange={e => { setFilters(f => ({ ...f, status: e.target.value })); setPage(1); }}>
            <option value="">Semua Status</option>
            <option value="active">Aktif</option>
            <option value="full">Penuh</option>
            <option value="maintenance">Maintenance</option>
            <option value="inactive">Nonaktif</option>
          </select>
          <select data-testid="odp-filter-branch"
            className={"h-9 border rounded-md px-3 text-sm " + (isBranchScoped ? "bg-slate-100 cursor-not-allowed" : "")}
            disabled={isBranchScoped}
            title={isBranchScoped ? 'Anda hanya dapat melihat data cabang Anda sendiri' : undefined}
            value={filters.branch_id}
            onChange={e => { if (isBranchScoped) return; setFilters(f => ({ ...f, branch_id: e.target.value })); setPage(1); }}>
            {!isBranchScoped && <option value="">Semua Cabang</option>}
            {filterOptions.branches.filter(b => !isBranchScoped || String(b.branch_id) === userBranchId).map((b, i) => (
              <option key={i} value={b.branch_id}>{b.branch_name}</option>
            ))}
          </select>
          <Button data-testid="odp-add-btn" onClick={openAdd} className="h-9 bg-blue-600 hover:bg-blue-700">
            <Plus className="w-4 h-4 mr-1" /> Tambah ODP
          </Button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg border overflow-hidden dark:bg-slate-800">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b dark:bg-slate-900">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-slate-600 dark:text-slate-300">Nama ODP</th>
                <th className="px-4 py-3 text-left font-medium text-slate-600 dark:text-slate-300">Alamat</th>
                <th className="px-4 py-3 text-left font-medium text-slate-600 dark:text-slate-300">Cabang</th>
                <th className="px-4 py-3 text-center font-medium text-slate-600 dark:text-slate-300">Kapasitas</th>
                <th className="px-4 py-3 text-center font-medium text-slate-600 dark:text-slate-300">Status</th>
                <th className="px-4 py-3 text-center font-medium text-slate-600 dark:text-slate-300">Customer</th>
                <th className="px-4 py-3 text-center font-medium text-slate-600 dark:text-slate-300">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-400 dark:text-slate-500">Memuat...</td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-400 dark:text-slate-500">Belum ada data ODP/POP</td></tr>
              ) : items.map(item => (
                <tr key={item.id} className="border-b hover:bg-slate-50/50 transition-colors">
                  <td className="px-4 py-3">
                    <div className="font-medium text-slate-800 dark:text-slate-200">{item.name}</div>
                    {item.latitude && <div className="text-xs text-slate-400 dark:text-slate-500">{item.latitude}, {item.longitude}</div>}
                  </td>
                  <td className="px-4 py-3 text-slate-600 max-w-[200px] truncate dark:text-slate-300">{item.address || '-'}</td>
                  <td className="px-4 py-3 text-slate-600 dark:text-slate-300">{item.branch_name || '-'}</td>
                  <td className="px-4 py-3 text-center">
                    <span className="font-medium">{item.used_ports || 0}</span>
                    <span className="text-slate-400 dark:text-slate-500">/{item.capacity || 0}</span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <Badge variant="outline" className={statusConfig[item.status]?.color || ''}>
                      {statusConfig[item.status]?.label || item.status}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <Badge variant="outline">{item.linked_customers || 0}</Badge>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <div className="flex items-center justify-center gap-1">
                      <Button variant="ghost" size="sm" onClick={() => openDetail(item.id)} data-testid={`odp-view-${item.id}`}>
                        <Eye className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => openEdit(item)} data-testid={`odp-edit-${item.id}`}>
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" className="text-red-500" onClick={() => handleDelete(item.id)} data-testid={`odp-delete-${item.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t bg-slate-50 dark:bg-slate-900">
            <span className="text-sm text-slate-500 dark:text-slate-400">Total: {total} ODP/POP</span>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="text-sm">{page} / {totalPages}</span>
              <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editItem ? 'Edit ODP/POP' : 'Tambah ODP/POP'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-sm font-medium text-slate-700 dark:text-slate-200">Nama ODP *</label>
              <Input data-testid="odp-form-name" value={formData.name} onChange={e => setFormData(f => ({ ...f, name: e.target.value }))} placeholder="ODP-001" />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 dark:text-slate-200">Alamat</label>
              <Input value={formData.address} onChange={e => setFormData(f => ({ ...f, address: e.target.value }))} placeholder="Jl. Contoh No. 123" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium text-slate-700 dark:text-slate-200">Latitude</label>
                <Input data-testid="odp-form-lat" value={formData.latitude} onChange={e => setFormData(f => ({ ...f, latitude: e.target.value }))} placeholder="-6.370" />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 dark:text-slate-200">Longitude</label>
                <Input data-testid="odp-form-lng" value={formData.longitude} onChange={e => setFormData(f => ({ ...f, longitude: e.target.value }))} placeholder="106.830" />
              </div>
            </div>
            <div>
              <MiniOdpMap
                latitude={formData.latitude}
                longitude={formData.longitude}
                onChange={({ lat, lng }) => setFormData(f => ({ ...f, latitude: String(lat), longitude: String(lng) }))}
                height={240}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium text-slate-700 dark:text-slate-200">Kapasitas Port</label>
                <Input data-testid="odp-form-capacity" type="number" value={formData.capacity} onChange={e => setFormData(f => ({ ...f, capacity: e.target.value }))} placeholder="8" />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 dark:text-slate-200">Status</label>
                <select className="w-full h-10 border rounded-md px-3 text-sm" value={formData.status} onChange={e => setFormData(f => ({ ...f, status: e.target.value }))}>
                  <option value="active">Aktif</option>
                  <option value="full">Penuh</option>
                  <option value="maintenance">Maintenance</option>
                  <option value="inactive">Nonaktif</option>
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium text-slate-700 dark:text-slate-200">Cabang</label>
                <select
                  data-testid="odp-form-branch"
                  value={formData.branch_id || ''}
                  disabled={isBranchScoped}
                  title={isBranchScoped ? 'Anda hanya dapat membuat ODP untuk cabang Anda sendiri' : undefined}
                  onChange={e => {
                    if (isBranchScoped) return;
                    const bid = e.target.value;
                    const b = filterOptions.branches.find(x => String(x.branch_id) === bid);
                    setFormData(f => ({ ...f, branch_id: bid, branch_name: b ? b.branch_name : '' }));
                  }}
                  className={"w-full h-10 border border-slate-200 rounded-md px-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 " + (isBranchScoped ? "bg-slate-100 cursor-not-allowed" : "bg-white")}
                >
                  {!isBranchScoped && <option value="">-- Pilih Cabang --</option>}
                  {filterOptions.branches.filter(b => !isBranchScoped || String(b.branch_id) === userBranchId).map((b, i) => (
                    <option key={i} value={b.branch_id}>{b.branch_name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 dark:text-slate-200">Owner</label>
                <select
                  data-testid="odp-form-owner"
                  value={formData.owner_id || ''}
                  onChange={e => {
                    const oid = e.target.value;
                    const o = filterOptions.owners.find(x => String(x.owner_id) === oid);
                    setFormData(f => ({ ...f, owner_id: oid, owner_name: o ? o.owner_name : '' }));
                  }}
                  className="w-full h-10 border border-slate-200 rounded-md px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-slate-700 dark:bg-slate-800"
                >
                  <option value="">-- Pilih Owner --</option>
                  {filterOptions.owners.map((o, i) => (
                    <option key={i} value={o.owner_id}>{o.owner_name}</option>
                  ))}
                </select>
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 dark:text-slate-200">Deskripsi</label>
              <Input value={formData.description} onChange={e => setFormData(f => ({ ...f, description: e.target.value }))} placeholder="Catatan..." />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForm(false)}>Batal</Button>
            <Button data-testid="odp-form-save" onClick={handleSave} className="bg-blue-600 hover:bg-blue-700">Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail Dialog with Customer Links */}
      <Dialog open={showDetail} onOpenChange={setShowDetail}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Network className="w-5 h-5 text-blue-600 dark:text-blue-400" /> {detailData?.name}
            </DialogTitle>
          </DialogHeader>
          {detailData && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><span className="text-slate-500 dark:text-slate-400">Alamat:</span> <span className="font-medium">{detailData.address || '-'}</span></div>
                <div><span className="text-slate-500 dark:text-slate-400">Status:</span> <Badge variant="outline" className={statusConfig[detailData.status]?.color}>{statusConfig[detailData.status]?.label}</Badge></div>
                <div><span className="text-slate-500 dark:text-slate-400">Koordinat:</span> <span className="font-medium">{detailData.latitude}, {detailData.longitude}</span></div>
                <div><span className="text-slate-500 dark:text-slate-400">Kapasitas:</span> <span className="font-medium">{detailData.used_ports}/{detailData.capacity} port</span></div>
                <div><span className="text-slate-500 dark:text-slate-400">Cabang:</span> <span className="font-medium">{detailData.branch_name || '-'}</span></div>
                <div><span className="text-slate-500 dark:text-slate-400">Owner:</span> <span className="font-medium">{detailData.owner_name || '-'}</span></div>
              </div>

              <div className="border-t pt-4">
                <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                  <h3 className="font-semibold text-slate-800 dark:text-slate-200">Customer Terhubung ({detailData.linked_customers?.length || 0})</h3>
                  <div className="flex items-center gap-2 flex-wrap">
                    <Button data-testid="odp-qr-btn" size="sm" variant="outline"
                      onClick={openQr}
                      className="border-indigo-200 text-indigo-700 hover:bg-indigo-50 dark:text-indigo-300 dark:hover:bg-indigo-900/30">
                      <QrCode className="w-4 h-4 mr-1" /> QR
                    </Button>
                    <Button data-testid="odp-export-pdf-btn" size="sm" variant="outline"
                      onClick={() => exportOdp('pdf')}
                      className="border-red-200 text-red-700 hover:bg-red-50 dark:text-red-300 dark:hover:bg-red-900/30">
                      <FileText className="w-4 h-4 mr-1" /> PDF
                    </Button>
                    <Button data-testid="odp-export-excel-btn" size="sm" variant="outline"
                      onClick={() => exportOdp('excel')}
                      className="border-emerald-200 text-emerald-700 hover:bg-emerald-50 dark:text-emerald-300 dark:hover:bg-emerald-900/30">
                      <FileSpreadsheet className="w-4 h-4 mr-1" /> Excel
                    </Button>
                    <Button data-testid="odp-link-customer-btn" size="sm" onClick={() => setShowLinkDialog(true)} className="bg-blue-600 hover:bg-blue-700">
                      <Link2 className="w-4 h-4 mr-1" /> Hubungkan Customer
                    </Button>
                  </div>
                </div>
                {detailData.linked_customers?.length > 0 ? (
                  <div className="space-y-2">
                    {detailData.linked_customers.map((c, i) => (
                      <div key={i} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border dark:bg-slate-900">
                        <div>
                          <span className="font-medium">{c.customer_name || 'N/A'}</span>
                          <span className="text-xs text-slate-400 ml-2 dark:text-slate-500">{c.account_number}</span>
                          {c.port_number && <Badge variant="outline" className="ml-2">Port {c.port_number}</Badge>}
                        </div>
                        <Button variant="ghost" size="sm" className="text-red-500" onClick={() => unlinkCustomer(c.customer_id)}>
                          <Unlink className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-slate-400 text-center py-4 dark:text-slate-500">Belum ada customer terhubung</p>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Link Customer Dialog */}
      <Dialog open={showLinkDialog} onOpenChange={setShowLinkDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Hubungkan Customer</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input data-testid="odp-customer-search" placeholder="Cari customer..." value={customerSearch}
              onChange={e => searchCustomers(e.target.value)} />
            <Input data-testid="odp-port-number" placeholder="Nomor port (opsional)" type="number"
              value={linkPort} onChange={e => setLinkPort(e.target.value)} />
            <div className="max-h-[200px] overflow-y-auto space-y-1">
              {customerResults.map(c => (
                <div key={c.id} className="flex items-center justify-between p-2 hover:bg-slate-50 rounded cursor-pointer border dark:hover:bg-slate-700/50"
                  onClick={() => linkCustomer(c.id)} data-testid={`link-customer-${c.id}`}>
                  <div>
                    <span className="font-medium text-sm">{c.name}</span>
                    <span className="text-xs text-slate-400 ml-2 dark:text-slate-500">{c.account_number}</span>
                  </div>
                  <Link2 className="w-4 h-4 text-blue-500" />
                </div>
              ))}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* QR Code Dialog */}
      <Dialog open={showQrDialog} onOpenChange={(open) => {
        setShowQrDialog(open);
        if (!open && qrState.objectUrl) {
          URL.revokeObjectURL(qrState.objectUrl);
          setQrState({ loading: false, url: null, objectUrl: null });
        }
      }}>
        <DialogContent className="max-w-sm" data-testid="odp-qr-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <QrCode className="w-5 h-5 text-indigo-600 dark:text-indigo-400" /> QR Code ODP
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="text-center">
              <p className="text-sm font-semibold text-slate-800 dark:text-slate-200">{detailData?.name}</p>
              <p className="text-xs text-slate-500 dark:text-slate-400">{detailData?.branch_name || '-'}</p>
            </div>
            <div className="flex items-center justify-center bg-white p-4 rounded-xl border-2 border-slate-200 dark:bg-slate-800 dark:border-slate-700">
              {qrState.loading ? (
                <div className="w-[240px] h-[240px] flex items-center justify-center text-slate-400 text-sm dark:text-slate-500">
                  Memuat QR...
                </div>
              ) : qrState.objectUrl ? (
                <img src={qrState.objectUrl} alt="QR Code" className="w-[240px] h-[240px]" data-testid="odp-qr-image" />
              ) : (
                <div className="w-[240px] h-[240px] flex items-center justify-center text-slate-400 text-sm dark:text-slate-500">
                  QR tidak tersedia
                </div>
              )}
            </div>
            {qrState.url && (
              <div className="bg-slate-50 rounded-md border px-3 py-2 text-[11px] text-slate-600 break-all text-center dark:bg-slate-900 dark:text-slate-300">
                {qrState.url}
              </div>
            )}
            <p className="text-xs text-slate-500 text-center leading-relaxed dark:text-slate-400">
              Tempel QR ini di box ODP fisik. Teknisi cukup scan pakai HP untuk membuka detail port secara real-time.
            </p>
            <div className="flex gap-2">
              <Button data-testid="odp-qr-download-btn" onClick={downloadQr} disabled={!qrState.objectUrl}
                className="flex-1 bg-indigo-600 hover:bg-indigo-700">
                <Download className="w-4 h-4 mr-1" /> Unduh PNG
              </Button>
              <Button data-testid="odp-qr-copy-btn" onClick={copyQrUrl} disabled={!qrState.url}
                variant="outline" className="flex-1">
                <Copy className="w-4 h-4 mr-1" /> Salin URL
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ODPManagement;
